import { Injectable, Inject } from '@angular/core';
import { ProfilesService } from '@icc/construction-elements/configurator';
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { FramesService } from './frames.service';
import { ActiveMullion } from '@icc/window-door/types';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/shared/config';;
import { SashTypes } from '@icc/window-door/types';
import { EventBusService } from '@icc/shared';

@Injectable()
export class SplitFrameService {
    constructor(
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private framesService: FramesService,
        private profilesService: ProfilesService,
        private eventBusService: EventBusService,
    ) {
        this.eventBusService.subscribe(['loadedProfiles'], data => {
            this.automaticallySplitFrames(data.activeConfiguration as WindowActiveConfiguration);
        });
    }

    automaticallySplitFrames(conf: WindowActiveConfiguration) {
        this.splitFrameContainingFixAndSash(conf);
        this.splitFrameContainingFixAndOutwardSash(conf);
    }

    splitLowThresholdFrameContainingFixAndSash(conf: WindowActiveConfiguration) {
        if (!this.config().IccConfig.Configurators.coupledFrames
            || conf.System.fix_and_low_threshold_behavior === 'split-bottom-frame-profile'
        ) {
            return;
        }

        const couplingProfiles = this.profilesService.getFilteredProfiles(conf, 'coupling', {});
        conf.Sashes.forEach(sash => {
            const frame = conf.Frames.find(f => f.id === sash.frameId);
            const multiAlignField = {
                left: 'multiAlignLeft',
                right: 'multiAlignRight',
                top: 'multiAlignTop',
                bottom: 'multiAlignBottom',
            };
            if (
                frame.lowThreshold
                && sash.nearMullions.bottom === -1
                && sash.nearAlignments.bottom === -1
            ) {
                ['left', 'right'].forEach(side => {
                    let mullion: ActiveMullion;
                    conf.Mullions.forEach(m => {
                        if (
                            m.id === sash.nearMullions[side]
                            && (m.rWidth === frame.width || m.rHeight === frame.height)
                        ) {
                            mullion = m;
                        }
                    });
                    if (mullion && couplingProfiles.length > 0) {
                        if (
                            sash.type.type === 'F'
                            && mullion[multiAlignField[side]].some(
                                s => (
                                    SashTypes.TURN_HUNG.indexOf(s.type.type) > -1
                                    || (this.config().IccConfig.Configurators.sliding_door.lowThresholdAvailable && ['HS', 'PSK'].indexOf(s.type.type) > -1)
                                )

                            )
                        ) {
                            this.framesService.splitFrame(couplingProfiles[0], mullion, conf);
                        }
                    }
                });
            }
        });
    }

    private splitFrameContainingFixAndSash(conf: WindowActiveConfiguration) {
        if (!this.config().IccConfig.Configurators.coupledFrames || !conf.System.split_fix_and_sash) {
            return;
        }

        const couplingProfiles = this.profilesService.getFilteredProfiles(conf, 'coupling', {});
        conf.Sashes.forEach(sash => {
            const frame = conf.Frames.find(f => f.id === sash.frameId);
            const multiAlignField = {
                left: 'multiAlignLeft',
                right: 'multiAlignRight',
                top: 'multiAlignTop',
                bottom: 'multiAlignBottom',
            };
            ['left', 'right', 'top', 'bottom'].forEach(side => {
                let mullion: ActiveMullion;
                conf.Mullions.forEach(m => {
                    if (
                        m.id === sash.nearMullions[side]
                        && (m.rWidth === frame.width || m.rHeight === frame.height)
                    ) {
                        mullion = m;
                    }
                });
                if (mullion && couplingProfiles.length > 0) {
                    if (sash.type.type === 'F') {
                        if (
                            mullion[multiAlignField[side]].some(
                                s => ['F', 'FF'].indexOf(s.type.type) === -1
                            )
                        ) {
                            this.framesService.splitFrame(
                                couplingProfiles[0],
                                mullion,
                                conf
                            );
                        }
                    } else {
                        if (
                            mullion[multiAlignField[side]].some(
                                s => ['F'].indexOf(s.type.type) > -1
                            )
                        ) {
                            this.framesService.splitFrame(
                                couplingProfiles[0],
                                mullion,
                                conf
                            );
                        }
                    }
                }
            });
        });
    }

    private splitFrameContainingFixAndOutwardSash(conf: WindowActiveConfiguration) {
        if (
            !this.config().IccConfig.Configurators.coupledFrames
            || !conf.System.split_fix_and_outward_sash
        ) {
            return;
        }

        const couplingProfiles = this.profilesService.getFilteredProfiles(conf, 'coupling', {});
        conf.Sashes.forEach(sash => {
            const frame = conf.Frames.find(f => f.id === sash.frameId);
            const multiAlignField = {
                left: 'multiAlignLeft',
                right: 'multiAlignRight',
                top: 'multiAlignTop',
                bottom: 'multiAlignBottom',
            };
            ['left', 'right', 'top', 'bottom'].forEach(side => {
                let mullion: ActiveMullion;
                conf.Mullions.forEach(m => {
                    if (
                        m.id === sash.nearMullions[side]
                        && (m.rWidth === frame.width || m.rHeight === frame.height)
                    ) {
                        mullion = m;
                    }
                });
                if (mullion && couplingProfiles.length > 0) {
                    if (sash.type.type === 'F') {
                        if (
                            mullion[multiAlignField[side]].some(
                                s => SashTypes.OUTWARD_OPENING.indexOf(s.type.type) > -1
                            )
                        ) {
                            this.framesService.splitFrame(
                                couplingProfiles[0],
                                mullion,
                                conf
                            );
                        }
                    } else if (SashTypes.OUTWARD_OPENING.indexOf(sash.type.type) > -1) {
                        if (
                            mullion[multiAlignField[side]].some(
                                s => ['F'].indexOf(s.type.type) > -1
                            )
                        ) {
                            this.framesService.splitFrame(
                                couplingProfiles[0],
                                mullion,
                                conf
                            );
                        }
                    }
                }
            });
        });
    }
}
